import React from 'react';

import Group from '@lightspeed/cirrus-group';
import Modal, { ModalBody, ModalFooter, ModalHeader } from '@lightspeed/cirrus-modal';
import { Button } from '@lightspeed/flame/Button';
import { IconPrinters } from '@lightspeed/flame/Icon/Printers';
import PropTypes from 'prop-types';

import Decimal from 'app/src/data/base/Decimal';
import Format from 'app/src/data/Format';
import Payment from 'app/src/data/models/Payment';

class TransactionResultModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showModal: true,
    };

    this.handleCloseModal = this.handleCloseModal.bind(this);
    this.handlePrintReceipt = this.handlePrintReceipt.bind(this);
    this.handleRetryPayment = this.handleRetryPayment.bind(this);
  }

  handleRetryPayment() {
    this.props.onRetryPayment(this.props.amount);
    this.handleCloseModal();
  }

  /**
   * Handles closing the modal.  Since we have closing animations, we need to make
   * sure we wait for that to finish before we unmount otherwise we'll get an
   * exception thrown by React.
   */
  handleCloseModal() {
    this.setState(
      {
        showModal: false,
      },
      () => {
        // In case the animation end was not triggered
        setTimeout(() => {
          this.props.onHide();
        }, 250);
      }
    );
  }

  handlePrintReceipt() {
    window.merchantos.print.printUrl({
      url:
        `/API/Account/${this.props.systemCustomerID}/DisplayTemplate/SalePayment/${this.props.salePaymentID}` +
        '.html?template=SalePayment&print=1&page_width=auto&page_height=2000mm',
      pageType: 'receipt',
    });
  }

  /**
   * Renders the modal subtitle.
   *
   * @returns {node}  The rendered subtitle.
   */
  renderPaymentSubtitle() {
    const { status } = this.props;

    let paymentStatus = '';

    switch (status) {
      case Payment.Status.CANCELLED:
        paymentStatus = 'Payment Cancelled';
        break;

      case Payment.Status.DECLINED:
        paymentStatus = 'Payment Declined';
        break;

      case Payment.Status.ERROR:
        paymentStatus = 'Payment Error';
        break;

      case Payment.Status.VOIDED:
        paymentStatus = 'The following payment has been voided';
        break;

      default:
        paymentStatus = 'Payment Failed';
    }

    return paymentStatus;
  }

  /**
   * Renders the payment status message.
   *
   * @returns {node}  The rendered payment status message.
   */
  renderPaymentMessage() {
    const { status, message, errorCode, paymentDetails, amount } = this.props;

    let paymentMessage = '';

    switch (status) {
      case Payment.Status.CANCELLED:
        paymentMessage = message || 'The payment has been cancelled by the customer';
        break;

      case Payment.Status.DECLINED:
        paymentMessage = message || 'The payment has been declined by the card issuer';
        break;

      case Payment.Status.ERROR:
        paymentMessage = message || `An error occurred: ${errorCode}`;
        break;

      case Payment.Status.VOIDED:
        paymentMessage = (
          <div>
            <var>{`${paymentDetails.cardBrand} `}</var>
            {`(${paymentDetails.maskedDigits}) ${Format.formatMoney(amount, 0)}`}
          </div>
        );
        break;

      default:
        break;
    }

    return paymentMessage;
  }

  /**
   * The React render method.
   *
   * @returns {node}  The rendered AddDepositModal
   */
  render() {
    const { showModal } = this.state;
    const { detailedMessage, status } = this.props;
    const paymentFailed =
      status === Payment.Status.CANCELLED ||
      status === Payment.Status.DECLINED ||
      status === Payment.Status.ERROR ||
      status === Payment.Status.FAILED;

    let closeButtonText = 'Cancel';
    if (status === Payment.Status.VOIDED) {
      closeButtonText = 'OK';
    }

    return (
      <Modal
        className="cirrus responsive transaction-result"
        isOpen={showModal}
        onRequestClose={this.handleCloseModal}
        shouldCloseOnOverlayClick={false}
      >
        <ModalHeader showCloseButton={false} className="transaction-result-header">
          <div className="transaction-result-title">
            {paymentFailed ? 'Payment Failed' : 'Credit/Debit'}
          </div>
        </ModalHeader>
        <ModalBody className="transaction-result-body">
          <div className="transaction-result-status">{this.renderPaymentSubtitle()}</div>
          <div className="transaction-result-section">{this.renderPaymentMessage()}</div>
          {detailedMessage && (
            <div className="transaction-result-section error">{detailedMessage}</div>
          )}
          <div className="transaction-result-section">
            <Button id="print-button" onClick={this.handlePrintReceipt}>
              <IconPrinters /> Print Receipt
            </Button>
          </div>
        </ModalBody>
        <ModalFooter className="transaction-result-footer">
          <Group className="cr-justify-end">
            <span className="transaction-footer-button">
              <Button id="close-button" onClick={this.handleCloseModal}>
                {closeButtonText}
              </Button>
            </span>
            {paymentFailed && (
              <span className="transaction-footer-button">
                <Button id="retry-button" variant="primary" fill onClick={this.handleRetryPayment}>
                  Retry
                </Button>
              </span>
            )}
          </Group>
        </ModalFooter>
      </Modal>
    );
  }
}

TransactionResultModal.propTypes = {
  amount: PropTypes.instanceOf(Decimal).isRequired,
  onHide: PropTypes.func.isRequired,
  status: PropTypes.string.isRequired,
};

export default TransactionResultModal;
